<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<i class="fa fa-map-marker animated bounceInDown show-info"></i>
						Attendance Locations
					</h3>
					<p class="text-muted" style="margin-left:4px">
						Sites employees may punch from. The radius is the fence; a per-site value overrides the
						company default.
					</p>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Add location
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th>Name</th>
							<th>Address</th>
							<th>Coordinates</th>
							<th>Radius</th>
							<th>Status</th>
							<th></th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="6" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!rows.length">
								<td colspan="6" class="text-center">
									No locations yet. Add one, or set every employee to geofence-exempt.
								</td>
							</tr>
							<tr v-for="row in rows" :key="row.id">
								<td>{{ row.name }}</td>
								<td>{{ row.address }}</td>
								<td>
									<a :href="mapsUrl(row)" target="_blank" rel="noopener" class="text-primary">
										{{ Number(row.lat).toFixed(5) }}, {{ Number(row.lng).toFixed(5) }}
									</a>
								</td>
								<td>{{ row.radius_m }} m</td>
								<td>
									<span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
										{{ row.is_active ? 'Active' : 'Inactive' }}
									</span>
								</td>
								<td style="white-space:nowrap">
									<button class="btn btn-sm btn-success" @click="showQr(row)" title="QR code">
										<i class="fa fa-qrcode"></i>
									</button>
									<button class="btn btn-sm btn-primary" @click="edit(row)" title="Edit">
										<i class="fa fa-pencil"></i>
									</button>
									<button class="btn btn-sm btn-danger" @click="remove(row)" title="Deactivate">
										<i class="fa fa-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<modal name="attendance-location" :width="520" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">{{ form.id ? 'Edit location' : 'Add location' }}</h4>
				<div class="form-group">
					<label>Name</label>
					<input type="text" class="form-control form-cascade-control" placeholder="Head Office" v-model="form.name" />
				</div>
				<div class="form-group">
					<label>Address</label>
					<input type="text" class="form-control form-cascade-control" v-model="form.address" />
				</div>
				<div class="row">
					<div class="col-md-6 form-group">
						<label>Latitude</label>
						<input type="number" step="0.0000001" class="form-control form-cascade-control" v-model.number="form.lat" />
					</div>
					<div class="col-md-6 form-group">
						<label>Longitude</label>
						<input type="number" step="0.0000001" class="form-control form-cascade-control" v-model.number="form.lng" />
					</div>
				</div>
				<button class="btn btn-default btn-sm" @click="useCurrentPosition">
					<i class="fa fa-map-marker"></i> Use my current location
				</button>
				<div class="form-group" style="margin-top:12px">
					<label>Radius (metres)</label>
					<input type="number" min="20" max="20000" class="form-control form-cascade-control" v-model.number="form.radius_m" />
					<small class="text-muted">
						Anything from 100–200 m suits a single building. Larger sites need more.
					</small>
				</div>
				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-location')">Cancel</button>
					<button class="btn btn-primary" :disabled="saving" @click="save">Save</button>
				</div>
			</div>
		</modal>

		<modal name="attendance-qr" :width="420" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px" v-if="qr.location">
				<h4 class="modal-header-title">Office QR - {{ qr.location.name }}</h4>
				<p class="text-muted" style="margin-bottom:12px">
					Display or print this at the site. Staff scan it to punch. Regenerate if it is ever
					leaked - every earlier copy stops working immediately.
				</p>

				<div id="attendance-qr-print" class="text-center" style="padding:12px">
					<img v-if="qr.dataUrl" :src="qr.dataUrl" alt="QR code" style="width:260px;height:260px" />
					<div v-else class="text-muted" style="padding:40px 0">Generating...</div>
					<div style="font-weight:bold;margin-top:8px">{{ qr.location.name }}</div>
					<div class="text-muted" style="font-size:12px">Scan to mark attendance</div>
				</div>

				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="regenerate" :disabled="qr.rotating" title="Regenerate">
						<i class="fa fa-refresh"></i> Regenerate
					</button>
					<button class="btn btn-primary" @click="printQr" :disabled="!qr.dataUrl">
						<i class="fa fa-print"></i> Print
					</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import QRCode from 'qrcode'
import { locationService, errorMessage } from './attendanceService'

export default {
	name: 'AttendanceLocations',
	data() {
		return {
			rows: [],
			loading: false,
			error: '',
			saving: false,
			form: this.blank(),
			// The QR modal: the site being shown, its rendered image, rotate state.
			qr: { location: null, dataUrl: '', rotating: false },
		}
	},
	mounted() {
		this.refresh()
	},
	methods: {
		blank() {
			return { id: null, name: '', address: '', lat: null, lng: null, radius_m: 200, is_active: true }
		},
		refresh() {
			this.loading = true
			this.error = ''
			locationService
				.list()
				.then((data) => {
					this.rows = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load locations.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		create() {
			this.form = this.blank()
			this.$modal.show('attendance-location')
		},
		edit(location) {
			this.form = { ...location, lat: Number(location.lat), lng: Number(location.lng) }
			this.$modal.show('attendance-location')
		},
		/**
		 * Fill the coordinates from the browser, so an admin standing at the site
		 * can capture the fence centre without looking anything up.
		 */
		useCurrentPosition() {
			if (!navigator.geolocation) {
				this.$notify({ text: 'Not supported by this browser', type: 'warn' })
				return
			}
			navigator.geolocation.getCurrentPosition(
				(position) => {
					this.$set(this.form, 'lat', Number(position.coords.latitude.toFixed(7)))
					this.$set(this.form, 'lng', Number(position.coords.longitude.toFixed(7)))
					this.$notify({ text: 'Coordinates captured', type: 'success' })
				},
				() => this.$notify({ text: 'Could not read your location', type: 'error' }),
				{ enableHighAccuracy: true, timeout: 10000 }
			)
		},
		save() {
			this.saving = true
			const body = { ...this.form }
			const id = body.id
			delete body.id
			const req = id ? locationService.update(id, body) : locationService.create(body)
			req
				.then(() => {
					this.$notify({ text: 'Saved', type: 'success' })
					this.$modal.hide('attendance-location')
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		remove(location) {
			if (
				!window.confirm(
					`Deactivate "${location.name}"? Past attendance that references it is kept.`
				)
			)
				return
			locationService
				.remove(location.id)
				.then(() => {
					this.$notify({ text: 'Deactivated', type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
		/** Open the QR modal for a site and render its code. */
		showQr(location) {
			this.qr = { location, dataUrl: '', rotating: false }
			this.$modal.show('attendance-qr')
			this.renderQr(location.qr_payload)
		},
		/** Draw the payload into a data URL for the modal and printout. */
		renderQr(payload) {
			if (!payload) {
				this.$notify({ text: 'No QR token for this site yet. Try Regenerate.', type: 'warn' })
				return
			}
			QRCode.toDataURL(payload, { width: 512, margin: 2, errorCorrectionLevel: 'M' })
				.then((url) => {
					this.qr.dataUrl = url
				})
				.catch(() => {
					this.$notify({ text: 'Could not render the QR code.', type: 'error' })
				})
		},
		/** Regenerate the token server-side, then re-render — old copies die. */
		regenerate() {
			if (!this.qr.location) return
			if (
				!window.confirm(
					'Regenerate this QR code? Every printout and screenshot of the current code stops working.'
				)
			)
				return
			this.qr.rotating = true
			locationService
				.rotateToken(this.qr.location.id)
				.then((data) => {
					this.qr.location = data
					this.qr.dataUrl = ''
					this.renderQr(data.qr_payload)
					// Keep the table's cached payload in step with the new token.
					const row = this.rows.find((r) => r.id === data.id)
					if (row) Object.assign(row, data)
					this.$notify({ text: 'QR code regenerated', type: 'success' })
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not regenerate'), type: 'error' })
				})
				.finally(() => {
					this.qr.rotating = false
				})
		},
		/** Print just the QR card in a clean popup window. */
		printQr() {
			const node = document.getElementById('attendance-qr-print')
			if (!node) return
			const win = window.open('', '_blank', 'width=480,height=600')
			if (!win) {
				this.$notify({ text: 'Allow pop-ups to print the code.', type: 'warn' })
				return
			}
			win.document.write(
				`<html><head><title>Attendance QR</title></head>` +
					`<body style="text-align:center;font-family:sans-serif;padding:24px">` +
					node.innerHTML +
					`</body></html>`
			)
			win.document.close()
			win.focus()
			win.onload = () => {
				win.print()
				win.close()
			}
			// Fallback if onload has already fired for the written document.
			setTimeout(() => {
				try {
					win.print()
				} catch (e) {
					/* window may be closed already */
				}
			}, 300)
		},
		mapsUrl(location) {
			return `https://www.google.com/maps?q=${location.lat},${location.lng}`
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
</style>
